import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { awsImageRootUrl } from "lib/constants";
import { Category, fetchCategory } from "lib/features/category/categoryService";
import { useGetIndustryTypesQuery } from "lib/features/industries/industriesService";
import { Industry } from "lib/features/industries/types";
import { CreateItemWithDetailDto } from "lib/features/item/types";
import { useUploadImageMutation } from "lib/features/s3/s3Service";
import { UploadImageRequest } from "lib/features/s3/types";
import { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { getCroppedImage } from "util/cropImage";
import { v4 as uuid } from "uuid";
import DurationPicker from "../../components/DurationPicker";
import Label from "../../components/Label";
import PostScript from "../../components/PostScript";
import detailStepValidation from "./DetailStep.schema";

interface DetailsStepProp {
  initialValues: CreateItemWithDetailDto;
  onSubmit: (values: CreateItemWithDetailDto) => void;
  setFormValues: any;
}

const DetailsStep = ({
  initialValues,
  onSubmit,
  setFormValues,
}: DetailsStepProp) => {
  const theme = useTheme();
  const [uploadImage] = useUploadImageMutation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<number>(1);

  // Cropper states
  const [openCropper, setOpenCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [currentFieldValue, setCurrentFieldValue] = useState<any>(null);

  const { data: industries } = useGetIndustryTypesQuery(null, {});
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const categories = await fetchCategory();
        setCategories(categories);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    getCategories();
  }, []);

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropConfirm = async (setFieldValue: any) => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const croppedBase64 = await getCroppedImage(imageSrc, croppedAreaPixels);
      if (croppedBase64) {
        const response = await resizeAndUploadImage(croppedBase64);
        if (response) {
          const imageUrl = `${response}`;

          // Update Formik values
          const updatedImages = [...currentFieldValue.images, imageUrl];
          setFieldValue("images", updatedImages);

          // Update global state
          setFormValues((prevValues: any) => ({
            ...prevValues,
            images: updatedImages,
          }));
        }
      }
    } catch (err) {
      console.error("Crop/Upload failed:", err);
    } finally {
      setOpenCropper(false);
    }
  };

  const resizeAndUploadImage = async (image: string) => {
    return new Promise((resolve, reject) => {
      const id = uuid() + ".jpg";
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");

      if (ctx && canvas) {
        const img = new Image();
        img.onload = async () => {
          const targetWidth = 1080;
          const scaleFactor = targetWidth / img.width;
          const targetHeight = img.height * scaleFactor;

          canvas.width = targetWidth;
          canvas.height = targetHeight;

          ctx.clearRect(0, 0, targetWidth, targetHeight);
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          // Convert canvas image to base64
          const base64String = canvas.toDataURL("image/jpeg").split(",")[1];
          const fileSize = atob(base64String).length;

          const payload: UploadImageRequest = {
            id,
            image: {
              base64: base64String,
              width: targetWidth,
              height: targetHeight,
              fileSize,
              uri: "",
              type: "image/jpeg",
              fileName: id,
            },
          };

          try {
            const result = await uploadImage(payload);
            resolve(result?.data);
          } catch (error) {
            console.log("Upload error:", error);
            reject(error);
          }
        };

        img.onerror = (err) => reject("Error loading image.");
        img.src = image;
      } else {
        reject("Canvas context is not available.");
      }
    });
  };

  useEffect(() => {
    if (selectedIndustry && categories) {
      let newCategories = categories?.filter(
        (category: Category) =>
          category?.category !== "All" &&
          (category?.category_type === selectedIndustry ||
            category?.category_type === 0)
      );
      setFilteredCategories(newCategories);
    }
  }, [categories, selectedIndustry]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={detailStepValidation}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ values, errors, touched, setFieldValue }) => {
        // Store current field value for cropper
        setCurrentFieldValue(values);

        const handleChange = (e: React.ChangeEvent<any>) => {
          const { name, value } = e.target;
          setFieldValue(name, value);
          setFormValues((prevValues: any) => ({
            ...prevValues,
            [name]: value,
          }));
        };

        const handleNumericChange = (
          e: React.ChangeEvent<any>,
          fieldName: string
        ) => {
          const { value } = e.target;
          if (/^\d*$/.test(value) || value === "") {
            const numericValue = value === "" ? null : Number(value);

            // Update Formik State
            setFieldValue(fieldName, numericValue);

            // Persist Data to Global State
            setFormValues((prevValues: any) => ({
              ...prevValues,
              [fieldName]: numericValue,
            }));
          }
        };

        const handleImageUpload = async (
          event: React.ChangeEvent<HTMLInputElement>
        ) => {
          const file = event.target.files?.[0];
          if (file && file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = async (e: ProgressEvent<FileReader>) => {
              if (e.target?.result) {
                // Open cropper with the raw image
                setImageSrc(e.target.result as string);
                setOpenCropper(true);
              }
            };
            reader.readAsDataURL(file);
          }
        };

        const handleDragEnd = (result: DropResult) => {
          if (!result.destination) return;

          const reordered = Array.from(values.images);
          const [moved] = reordered.splice(result.source.index, 1);
          reordered.splice(result.destination.index, 0, moved);

          setFieldValue("images", reordered);
          setFormValues((prevValues: any) => ({
            ...prevValues,
            images: reordered,
          }));
        };

        const handleRemoveImage = (index: number) => {
          const newFormImages = (values.images || []).filter(
            (_: string, i: number) => i !== index
          );
          setFieldValue("images", newFormImages);
          setFormValues((prevValues: any) => ({
            ...prevValues,
            images: newFormImages,
          }));
        };

        const handleSelectIndustryCategory = (industry_id: number) => {
          setSelectedIndustry(industry_id);
          if (!values.industry_ids.includes(industry_id)) {
            setFieldValue("industry_ids", [
              ...values.industry_ids,
              industry_id,
            ]);

            setFormValues((prevValues: any) => ({
              ...prevValues,
              industry_ids: [...prevValues.industry_ids, industry_id],
            }));
          }
        };

        const handleSelectCategory = (category_id: number) => {
          const isCategorySelected = values.category_ids.includes(category_id);

          if (isCategorySelected) {
            const newCategoryIds = values.category_ids.filter(
              (id) => id !== category_id
            );
            setFieldValue("category_ids", newCategoryIds);

            setFormValues((prevValues: any) => ({
              ...prevValues,
              category_ids: newCategoryIds,
            }));
          } else {
            setFieldValue("category_ids", [
              ...values.category_ids,
              category_id,
            ]);

            setFormValues((prevValues: any) => ({
              ...prevValues,
              category_ids: [...prevValues.category_ids, category_id],
            }));
          }
        };

        const handleDurationChange = (
          hours: number | null,
          minutes: number | null
        ) => {
          setFieldValue("duration_hours", hours);
          setFieldValue("duration_minutes", minutes);

          setFormValues((prevValues: any) => ({
            ...prevValues,
            duration_hours: hours,
            duration_minutes: minutes,
          }));
        };

        const handleDurationApplicableChange = (checked: boolean) => {
          setFieldValue("duration_applicable", checked);
          setFormValues((prevValues: any) => ({
            ...prevValues,
            duration_applicable: checked,
          }));

          if (checked) {
            setFieldValue("duration_hours", null);
            setFieldValue("duration_minutes", null);
            setFormValues((prevValues: any) => ({
              ...prevValues,
              duration_hours: null,
              duration_minutes: null,
            }));
          }
        };

        return (
          <Form>
            <Box sx={{ fontFamily: theme.typography.fontFamily }}>
              {/* Item Name */}
              <Box>
                <Label label="Item Name" required={true} />
                <Field
                  as={TextField}
                  name="item_name"
                  fullWidth
                  onChange={handleChange}
                  error={touched.item_name && Boolean(errors.item_name)}
                  multiline
                  minRows={1}
                  maxRows={2}
                />
                {errors.item_name && touched.item_name && (
                  <Typography color="red" fontSize="12px">
                    {errors.item_name}
                  </Typography>
                )}
                <PostScript
                  text="Item name should be brief and descriptive."
                  italics='Ex: "One Hour Guided Jet Ski Tour"'
                />
              </Box>

              {/* Headline */}
              <Box>
                <Label label="Headline" required={true} />
                <Field
                  as={TextField}
                  name="headline"
                  fullWidth
                  onChange={handleChange}
                  error={touched.headline && Boolean(errors.headline)}
                  inputProps={{ maxLength: 85 }}
                />

                <Box>
                  {errors.headline && touched.headline && (
                    <Typography color="red" fontSize="12px">
                      {errors.headline}
                    </Typography>
                  )}
                </Box>
                <Typography
                  fontSize="12px"
                  color={values.headline?.length >= 85 ? "red" : "gray"}
                  fontWeight={values.headline?.length >= 85 ? "normal" : "normal"}
                  display={values.headline?.length >= 85 ? "" : "none"}
                >
                  Headline must not exceed 85 characters.
                </Typography>

                <PostScript
                  text="List the most important details in 85 characters"
                  italics="Ex: $1000 | 32 passengers | BYOB | coolers+ice included"
                />
              </Box>

              {/* Item Images */}
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: "bold", marginTop: "1rem" }}
                >
                  Item Photos *
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    marginBottom: "0.5rem",
                    marginTop: "-0.5rem",
                  }}
                >
                  Upload up to four photos of your item.
                </Typography>

                <Box sx={{ marginTop: "1rem" }}>
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="images" direction="horizontal">
                      {(provided) => (
                        <Box
                          display="flex"
                          gap={2}
                          mt={2}
                          flexWrap="wrap"
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {values.images.length === 0 ? (
                            <Button
                              component="label"
                              sx={{
                                border: "2px dashed gray",
                                height: 200,
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                borderRadius: "8px",
                                backgroundColor: "transparent",
                              }}
                            >
                              <AddCircleIcon sx={{ fontSize: 40, color: "green" }} />
                              <Typography>Tap to Upload up to four photos</Typography>
                              <input
                                hidden
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                              />
                            </Button>
                          ) : (
                            values.images.map((image: string, index: number) => (
                              <Draggable
                                key={image}
                                draggableId={image}
                                index={index}
                              >
                                {(provided) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    position="relative"
                                    flex={index === 0 ? "1 1 100%" : "1 1 30%"}
                                    maxWidth={index === 0 ? "100%" : "33%"}
                                    height={index === 0 ? 200 : 150}
                                  >
                                    <img
                                      src={`${awsImageRootUrl}/${image}`}
                                      alt={`thumb-${index}`}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        borderRadius: 8,
                                      }}
                                    />
                                    <IconButton
                                      size="small"
                                      sx={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        bgcolor: "white",
                                      }}
                                      onClick={() => handleRemoveImage(index)}
                                    >
                                      <CancelIcon fontSize="small" color="action" />
                                    </IconButton>
                                  </Box>
                                )}
                              </Draggable>
                            ))
                          )}
                          {provided.placeholder}
                        </Box>
                      )}
                    </Droppable>
                  </DragDropContext>

                  {/* Upload More */}
                  {values.images.length > 0 && values.images.length < 4 && (
                    <Box mt={2}>
                      <Button
                        component="label"
                        variant="outlined"
                        sx={{ borderStyle: "dashed" }}
                      >
                        <AddCircleIcon sx={{ mr: 1 }} />
                        Upload more photos
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                        />
                      </Button>
                    </Box>
                  )}

                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={500}
                    style={{ display: "none" }}
                  />

                  {errors.images && touched.images && (
                    <Typography color="red" fontSize="12px">
                      {errors.images}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Cropper Dialog */}
              <Dialog
                open={openCropper}
                onClose={() => setOpenCropper(false)}
                maxWidth="sm"
                fullWidth
              >
                <DialogContent
                  sx={{ position: "relative", height: 400, background: "#333" }}
                >
                  {imageSrc && (
                    <Cropper
                      image={imageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={4 / 3} // You can adjust this aspect ratio for items
                      cropShape="rect"
                      rotation={0}
                      showGrid={true}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                    />
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenCropper(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={() => handleCropConfirm(setFieldValue)}
                  >
                    Crop & Upload
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Selling Points */}
              <Box sx={{ marginTop: "3rem" }}>
                <Label
                  label="Selling Points and Important Info"
                  required={false}
                />
                <Typography>(Optional & Only Visible to Referees)</Typography>
                <Field
                  as={TextField}
                  name="selling_points"
                  fullWidth
                  multiline
                  rows={3}
                  onChange={(e: any) => {
                    const text = e.target.value;
                    const lines = text.split("\n");

                    // Process each line
                    const processedLines = lines.map(
                      (line: any, index: number) => {
                        if (line.trim() === "" && index !== lines.length - 1) {
                          return "";
                        }

                        const cleanLine = line.replace(/^•\s?/, "");

                        return cleanLine.trim() === "" ? "" : `• ${cleanLine}`;
                      }
                    );
                    const bulletText = processedLines.join("\n");
                    setFieldValue("selling_points", bulletText);
                    setFormValues((prevValues: any) => ({
                      ...prevValues,
                      selling_points: bulletText,
                    }));
                  }}
                />
                <PostScript text="Let Referees know a few key points that will help sell this item! You can also add things like price is negotiable or times are flexible." />
              </Box>

              {/* Industries */}
              <Box sx={{ marginTop: "1rem" }}>
                <Label label="Select Industries" required={true} />
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {industries &&
                    industries?.map((industryType: Industry) => {
                      const _selected = selectedIndustry === industryType?.id;

                      return (
                        <Chip
                          key={industryType.id}
                          label={industryType.industry}
                          onClick={() =>
                            handleSelectIndustryCategory(industryType.id)
                          }
                          variant={_selected ? "filled" : "outlined"}
                          color={_selected ? "primary" : "default"}
                          sx={{
                            margin: "0 0.5rem 0.5rem 0",
                            border: _selected ? "none" : "1px solid #000",
                          }}
                        />
                      );
                    })}
                </Box>
                {errors.industry_ids && touched.industry_ids && (
                  <Typography color="red" fontSize="12px">
                    {errors.industry_ids}
                  </Typography>
                )}
              </Box>

              {/* Categories */}
              <Box sx={{ marginTop: "1rem" }}>
                <Label label="Select Categories" required={true} />
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {filteredCategories &&
                    filteredCategories?.map((category: Category) => {
                      const _selected = values.category_ids.includes(
                        category?.id
                      );

                      return (
                        <Chip
                          key={category.id}
                          label={category.category}
                          onClick={() => handleSelectCategory(category.id)}
                          variant={_selected ? "filled" : "outlined"}
                          color={_selected ? "primary" : "default"}
                          sx={{
                            margin: "0 0.5rem 0.5rem 0",
                            border: _selected ? "none" : "1px solid #000",
                            cursor: "pointer",
                          }}
                        />
                      );
                    })}
                </Box>
                {errors.category_ids && touched.category_ids && (
                  <Typography color="red" fontSize="12px">
                    {errors.category_ids}
                  </Typography>
                )}
              </Box>

              {/* Description */}
              <Box>
                <Label label="Description" required={true} />
                <Field
                  as={TextField}
                  name="description"
                  fullWidth
                  multiline
                  rows={3}
                  onChange={handleChange}
                  error={touched.description && Boolean(errors.description)}
                />
                {errors.description && touched.description && (
                  <Typography color="red" fontSize="12px">
                    {errors.description}
                  </Typography>
                )}
                <PostScript text="3 to 5 sentences describing the item." />
              </Box>

              {/* Duration Picker */}
              <Box>
                <Label label="Duration" required={true} />
                <DurationPicker
                  initialSelectedHours={values.duration_hours}
                  initialSelectedMinutes={values.duration_minutes}
                  onChange={handleDurationChange}
                  duration_applicable={values.duration_applicable}
                  handleDurationApplicableChange={
                    handleDurationApplicableChange
                  }
                  errors={errors}
                  touched={touched}
                />
              </Box>

              {/* What's Included */}
              <Box>
                <Label label="What's Included" required={true} />
                <Field
                  as={TextField}
                  name="whats_included"
                  fullWidth
                  multiline
                  rows={3}
                  onChange={handleChange}
                  error={touched.whats_included && Boolean(errors.whats_included)}
                />
                {errors.whats_included && touched.whats_included && (
                  <Typography color="red" fontSize="12px">

                    {errors.whats_included}
                  </Typography>
                )}
                <PostScript text="Anything that is included with the base price of the item." />
              </Box>

              {/* Cancellation Policy */}
              <Box>
                <Label label="Cancellation Policy" required={true} />
                <Field
                  as={TextField}
                  name="cancellation_policy"
                  fullWidth
                  multiline
                  rows={3}
                  onChange={handleChange}
                  error={
                    touched.cancellation_policy &&
                    Boolean(errors.cancellation_policy)
                  }
                />
                {errors.cancellation_policy && touched.cancellation_policy && (
                  <Typography color="red" fontSize="12px">
                    {errors.cancellation_policy}
                  </Typography>
                )}
              </Box>

              <Typography
                sx={{ fontSize: "24px", fontWeight: "600", marginY: "10px" }}
              >
                Optional Fields
              </Typography>

              {/* Additional Info */}
              <Box>
                <Label label="Additional Info" required={false} />
                <Field
                  as={TextField}
                  name="additional_info"
                  fullWidth
                  multiline
                  rows={3}
                  onChange={handleChange}
                  error={
                    touched.additional_info && Boolean(errors.additional_info)
                  }
                />
                {errors.additional_info && touched.additional_info && (
                  <Typography color="red" fontSize="12px">
                    {errors.additional_info}
                  </Typography>
                )}
                <PostScript
                  text="Any other information that you may have missed in 'Description' and 'Whats Included.'"
                  italics="Ex: What to bring, what to expect, etc."
                />
              </Box>

              {/* Max No. of Guests */}
              <Box>
                <Label label="Max Number of Guests" required={false} />
                <Field
                  as={TextField}
                  type="number"
                  name="max_guest"
                  fullWidth
                  onChange={(e: any) => handleNumericChange(e, "max_guest")}
                  error={touched.max_guest && Boolean(errors.max_guest)}
                />

                {errors.max_guest && touched.max_guest && (
                  <Typography color="red" fontSize="12px">
                    {errors.max_guest}
                  </Typography>
                )}
                <PostScript text="Optional if you are selling individual tickets.  Enter max capacity if offering something like a boat charter or party bus." />
              </Box>

              {/* Min Max Age */}
              <Label label="Age Restrictions" required={false} />
              <Box sx={{ display: "flex", gap: "10px" }}>
                {/* Minimum Age */}
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Label label="Minimum Age" required={false} />
                  <Field
                    as={TextField}
                    type="numeric"
                    name="min_age"
                    fullWidth
                    onChange={(e: any) => handleNumericChange(e, "min_age")}
                    error={touched.min_age && Boolean(errors.min_age)}
                  />
                </Box>

                {/* Maximum Age */}
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                  <Label label="Maximum Age" required={false} />
                  <Field
                    as={TextField}
                    type="number"
                    name="max_age"
                    fullWidth
                    onChange={(e: any) => handleNumericChange(e, "max_age")}
                    error={touched.max_age && Boolean(errors.max_age)}
                  />
                </Box>
              </Box>

              <Button
                type="submit"
                variant="contained"
                sx={{ width: "100%", marginTop: "20px", paddingY: "10px" }}
              >
                Continue
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default DetailsStep;